import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChartConfiguration } from 'chart.js';
import { forkJoin } from 'rxjs';
import { MONTHS } from 'src/app/models/MONTHS';
import { OverheadCategoryWithFinancialData } from 'src/app/models/OverheadCategoryWithFinancialData';
import { CalendarClickEventData, LegendPosition } from 'src/app/shared/charts/default-line-chart/default-line-chart.component';
import { capitalizeFirstLetter } from 'src/app/shared/utils/string-utils';

import { ChartInformationModalComponent, ChartInformationObjectType } from '../chart-information-modal/chart-information-modal.component';
import { DATASETS } from '../employee-graph/employee-graph.component';
import { FinancialService } from '../financial.service';

interface BudgetMonthDetails {
    month: number;
    label: string;
    budget: number;
    spent: number;
}

@Component({
    selector: 'app-overhead-category-budget-details-modal',
    templateUrl: './overhead-category-budget-details-modal.component.html',
    styleUrls: ['./overhead-category-budget-details-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class OverheadCategoryBudgetDetailsModalComponent implements OnInit {
    @Input() categoryId: string;
    @Input() categoryName: string;
    @Input() year: number;

    selectedSource: 'YUKI' | 'ALLOCATIONS' = 'ALLOCATIONS';

    loading = false;
    rows: BudgetMonthDetails[] = [];
    budgetByMonth: number[] = Array.from({ length: 12 }, () => 0);
    spentByMonth: number[] = Array.from({ length: 12 }, () => 0);
    chartData: number[][] = [];
    chartLabels: string[] = ['Actual spent', 'Budget (monthly)'];
    chartXLabels: string[] = [];
    legendPosition: LegendPosition = LegendPosition.BOTTOM;
    chartOptions: ChartConfiguration['options'] = {
        maintainAspectRatio: true,
        aspectRatio: 4,
    };

    readonly months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' },
    ];

    constructor(public activeModal: NgbActiveModal, private financialService: FinancialService, private modalService: NgbModal) {}

    ngOnInit(): void {
        this.chartXLabels = this.seedLabels();
        this.load();
    }

    openPointDetails(event: CalendarClickEventData): void {
        if (!event || event.month == null || !this.categoryId) {
            return;
        }

        const modalRef = this.modalService.open(ChartInformationModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });

        modalRef.componentInstance.month = event.month;
        modalRef.componentInstance.year = this.year;
        modalRef.componentInstance.dataset = DATASETS.COST;
        modalRef.componentInstance.chartInformationObjectType = ChartInformationObjectType.CATEGORY;
        modalRef.componentInstance.objectId = this.categoryId;
        modalRef.componentInstance.title = this.categoryName || 'Overhead category';
    }

    getProgressWidth(row: BudgetMonthDetails): number {
        if (!row || row.budget <= 0 || row.spent <= 0) return 0;
        return Math.min(100, (row.spent / row.budget) * 100);
    }

    getProgressClass(row: BudgetMonthDetails): string {
        if (!row || row.budget <= 0) return 'bar-fill bar-fill--none';
        if (row.spent > row.budget) return 'bar-fill bar-fill--over';
        return 'bar-fill bar-fill--ok';
    }

    get yearlyBudgetTotal(): number {
        return this.rows.reduce((sum, row) => sum + Number(row.budget || 0), 0);
    }

    get yearlySpentTotal(): number {
        return this.spentByMonth.reduce((sum, value) => sum + Number(value || 0), 0);
    }

    get yearlyProgressWidth(): number {
        if (this.yearlyBudgetTotal <= 0 || this.yearlySpentTotal <= 0) return 0;
        return Math.min(100, (this.yearlySpentTotal / this.yearlyBudgetTotal) * 100);
    }

    get yearlyProgressClass(): string {
        if (this.yearlyBudgetTotal <= 0) return 'bar-fill bar-fill--none';
        if (this.yearlySpentTotal > this.yearlyBudgetTotal) return 'bar-fill bar-fill--over';
        return 'bar-fill bar-fill--ok';
    }

    private load(): void {
        if (!this.categoryId || !this.year) {
            this.rows = this.months.map((month) => ({ month: month.value, label: month.label, budget: 0, spent: 0 }));
            this.budgetByMonth = Array.from({ length: 12 }, () => 0);
            this.spentByMonth = Array.from({ length: 12 }, () => 0);
            this.chartData = [this.spentByMonth, this.budgetByMonth];
            return;
        }

        this.loading = true;
        forkJoin({
            budgets: this.financialService.getOverheadCategoryMonthlyBudgets(this.categoryId, this.year),
            financialData: this.financialService.fetchFinancialDataForOverheadCategoryByCategoryId(this.categoryId, this.year),
        }).subscribe({
            next: ({ budgets, financialData }) => {
                this.budgetByMonth = this.mapBudgetByMonth(budgets || []);
                this.spentByMonth = this.mapSpentByMonth(financialData);
                this.chartData = [this.spentByMonth, this.budgetByMonth];
                this.rows = this.mapRows(budgets || [], this.spentByMonth);
                this.loading = false;
            },
            error: () => {
                this.rows = this.months.map((month) => ({ month: month.value, label: month.label, budget: 0, spent: 0 }));
                this.budgetByMonth = Array.from({ length: 12 }, () => 0);
                this.spentByMonth = Array.from({ length: 12 }, () => 0);
                this.chartData = [this.spentByMonth, this.budgetByMonth];
                this.loading = false;
            },
        });
    }

    private mapRows(budgets: Array<{ month: number; budget: number }>, spentByMonth: number[]): BudgetMonthDetails[] {
        const budgetMap = new Map<number, number>();
        budgets.forEach((entry) => budgetMap.set(entry.month, Number(entry.budget || 0)));

        return this.months.map((month) => ({
            month: month.value,
            label: month.label,
            budget: budgetMap.get(month.value) ?? 0,
            spent: Number(spentByMonth[month.value - 1] || 0),
        }));
    }

    private mapSpentByMonth(financialData: OverheadCategoryWithFinancialData | null): number[] {
        const result = Array.from({ length: 12 }, (_, index) => Number(financialData?.financialData?.[index] || 0));
        return result;
    }

    private mapBudgetByMonth(budgets: Array<{ month: number; budget: number }>): number[] {
        const budgetMap = new Map<number, number>();
        budgets.forEach((entry) => budgetMap.set(entry.month, Number(entry.budget || 0)));
        return Array.from({ length: 12 }, (_, index) => Number(budgetMap.get(index + 1) || 0));
    }

    private seedLabels(): string[] {
        return Object.values(MONTHS).map((label) => capitalizeFirstLetter(label.short));
    }
}
